/**
 * Binary reader and writer.
 *
 * Every packet on the wire is written by hand rather than serialised generically.
 * At 500 players receiving position updates ten times a second, the difference
 * between a tight encoding and a generic one is the difference between a shard that
 * fits on one machine and one that does not.
 *
 * Both sides use these same primitives, so a field written with writeSmall must be
 * read with readSmall. The packet definitions in this package are the only place
 * that ordering is decided.
 */

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

export class ByteWriter {
  private view: DataView;
  private bytes: Uint8Array;
  private offset = 0;

  constructor(initialCapacity = 256) {
    this.bytes = new Uint8Array(initialCapacity);
    this.view = new DataView(this.bytes.buffer);
  }

  private ensure(extra: number): void {
    const needed = this.offset + extra;
    if (needed <= this.bytes.length) return;

    let capacity = this.bytes.length * 2;
    while (capacity < needed) capacity *= 2;

    const grown = new Uint8Array(capacity);
    grown.set(this.bytes);
    this.bytes = grown;
    this.view = new DataView(grown.buffer);
  }

  get length(): number {
    return this.offset;
  }

  writeU8(value: number): this {
    this.ensure(1);
    this.view.setUint8(this.offset, value & 0xff);
    this.offset += 1;
    return this;
  }

  writeI8(value: number): this {
    this.ensure(1);
    this.view.setInt8(this.offset, value);
    this.offset += 1;
    return this;
  }

  writeU16(value: number): this {
    this.ensure(2);
    this.view.setUint16(this.offset, value, true);
    this.offset += 2;
    return this;
  }

  writeI16(value: number): this {
    this.ensure(2);
    this.view.setInt16(this.offset, value, true);
    this.offset += 2;
    return this;
  }

  writeU32(value: number): this {
    this.ensure(4);
    this.view.setUint32(this.offset, value >>> 0, true);
    this.offset += 4;
    return this;
  }

  writeI32(value: number): this {
    this.ensure(4);
    this.view.setInt32(this.offset, value | 0, true);
    this.offset += 4;
    return this;
  }

  writeF32(value: number): this {
    this.ensure(4);
    this.view.setFloat32(this.offset, value, true);
    this.offset += 4;
    return this;
  }

  writeF64(value: number): this {
    this.ensure(8);
    this.view.setFloat64(this.offset, value, true);
    this.offset += 8;
    return this;
  }

  /** Unsigned LEB128. One byte for values under 128, which covers most counts and ids. */
  writeVarInt(value: number): this {
    let remaining = value >>> 0;
    do {
      let byte = remaining & 0x7f;
      remaining >>>= 7;
      if (remaining !== 0) byte |= 0x80;
      this.writeU8(byte);
    } while (remaining !== 0);
    return this;
  }

  /** Zigzag then LEB128, so small negative values stay small. */
  writeVarIntSigned(value: number): this {
    return this.writeVarInt(((value << 1) ^ (value >> 31)) >>> 0);
  }

  writeBool(value: boolean): this {
    return this.writeU8(value ? 1 : 0);
  }

  /** Length prefixed UTF-8. */
  writeString(value: string): this {
    const encoded = textEncoder.encode(value);
    this.writeVarInt(encoded.length);
    this.ensure(encoded.length);
    this.bytes.set(encoded, this.offset);
    this.offset += encoded.length;
    return this;
  }

  writeBytes(value: Uint8Array): this {
    this.writeVarInt(value.length);
    this.ensure(value.length);
    this.bytes.set(value, this.offset);
    this.offset += value.length;
    return this;
  }

  /** A copy of exactly the bytes written, ready to hand to a socket. */
  toUint8Array(): Uint8Array {
    return this.bytes.slice(0, this.offset);
  }

  reset(): void {
    this.offset = 0;
  }
}

export class ByteReader {
  private readonly view: DataView;
  private readonly bytes: Uint8Array;
  private offset = 0;

  constructor(source: ArrayBuffer | Uint8Array) {
    this.bytes = source instanceof Uint8Array ? source : new Uint8Array(source);
    this.view = new DataView(this.bytes.buffer, this.bytes.byteOffset, this.bytes.byteLength);
  }

  get remaining(): number {
    return this.bytes.byteLength - this.offset;
  }

  get exhausted(): boolean {
    return this.offset >= this.bytes.byteLength;
  }

  private require(count: number): void {
    // A client can send anything at all, so every read is bounds checked and a short
    // packet throws rather than reading whatever happens to be adjacent in memory.
    if (this.offset + count > this.bytes.byteLength) {
      throw new RangeError(
        `packet ended early: wanted ${count} bytes at offset ${this.offset} of ${this.bytes.byteLength}`,
      );
    }
  }

  readU8(): number {
    this.require(1);
    const value = this.view.getUint8(this.offset);
    this.offset += 1;
    return value;
  }

  readI8(): number {
    this.require(1);
    const value = this.view.getInt8(this.offset);
    this.offset += 1;
    return value;
  }

  readU16(): number {
    this.require(2);
    const value = this.view.getUint16(this.offset, true);
    this.offset += 2;
    return value;
  }

  readI16(): number {
    this.require(2);
    const value = this.view.getInt16(this.offset, true);
    this.offset += 2;
    return value;
  }

  readU32(): number {
    this.require(4);
    const value = this.view.getUint32(this.offset, true);
    this.offset += 4;
    return value;
  }

  readI32(): number {
    this.require(4);
    const value = this.view.getInt32(this.offset, true);
    this.offset += 4;
    return value;
  }

  readF32(): number {
    this.require(4);
    const value = this.view.getFloat32(this.offset, true);
    this.offset += 4;
    return value;
  }

  readF64(): number {
    this.require(8);
    const value = this.view.getFloat64(this.offset, true);
    this.offset += 8;
    return value;
  }

  readVarInt(): number {
    let result = 0;
    let shift = 0;
    for (;;) {
      const byte = this.readU8();
      result |= (byte & 0x7f) << shift;
      if ((byte & 0x80) === 0) break;
      shift += 7;
      if (shift > 35) throw new RangeError('varint is too long');
    }
    return result >>> 0;
  }

  readVarIntSigned(): number {
    const raw = this.readVarInt();
    return (raw >>> 1) ^ -(raw & 1);
  }

  readBool(): boolean {
    return this.readU8() !== 0;
  }

  readString(maxLength = 4096): string {
    const length = this.readVarInt();
    if (length > maxLength)
      throw new RangeError(`string of ${length} exceeds the ${maxLength} cap`);
    this.require(length);
    const value = textDecoder.decode(this.bytes.subarray(this.offset, this.offset + length));
    this.offset += length;
    return value;
  }

  readBytes(maxLength = 65_536): Uint8Array {
    const length = this.readVarInt();
    if (length > maxLength) throw new RangeError(`blob of ${length} exceeds the ${maxLength} cap`);
    this.require(length);
    const value = this.bytes.slice(this.offset, this.offset + length);
    this.offset += length;
    return value;
  }
}
